import React, { useState } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, TextField, Typography,
} from '@mui/material';
import { Translate } from 'react-localize-redux';
import type { ImageBytes } from '../QualityCheck/imageMeta';
import { decodePlacementRefusal, lesionTitle, PhotoToPlace, PlacementRefusal, PlacementTarget } from './photoPlacement';
import PlacementPhoto from './PlacementPhoto';
import LesionFacts from './LesionFacts';

const T = 'hospital.patient.photo-placement';

/** The slot a photo goes to. */
export interface PlacementChoice { groupRecordUuid: string; field: string }

interface PhotoPlacementDialogProps {
    photo: PhotoToPlace;
    bytes: ImageBytes | undefined;
    /** where a moved photo is now, e.g. "C-Lesion #1 · Macro" */
    sourceLabel?: string;
    targets: PlacementTarget[];
    /** sends the move / attach; rejects with the API's refusal */
    onSubmit: (choice: PlacementChoice, reason: string) => Promise<unknown>;
    onClose: () => void;
}

const choiceKey = (choice: PlacementChoice) => `${choice.groupRecordUuid}|${choice.field}`;

/**
 * SKIN-576 — where a photo goes, and why. The reviewer picks a slot of another lesion (a slot
 * the lesion hides is shown disabled, with the reason) and writes a reason; there is no default.
 * A move is sent from there. An attach goes through a second step that shows the photo next to
 * the target lesion, and only that step's button sends it: attaching the wrong patient's photo is
 * a clinical data error, and the orphan list is a heuristic (David, 2026-10-05).
 */
export default function PhotoPlacementDialog({ photo, bytes, sourceLabel, targets, onSubmit, onClose }: PhotoPlacementDialogProps) {
    const [choice, setChoice] = useState<PlacementChoice | null>(null);
    const [reason, setReason] = useState('');
    const [step, setStep] = useState<'choose' | 'confirm'>('choose');
    const [error, setError] = useState<PlacementRefusal | null>(null);
    const [busy, setBusy] = useState(false);

    const chosen = choice && targets.find((target) => target.lesion.groupRecordUuid === choice.groupRecordUuid);
    const chosenSlot = chosen?.slots.find((slot) => slot.name === choice?.field);

    const send = async () => {
        if (!choice) return;
        setBusy(true);
        setError(null);
        try {
            await onSubmit(choice, reason.trim());
        } catch (refusal) {
            setError(decodePlacementRefusal(refusal));
            setBusy(false);
            return;
        }
        onClose();
    };

    const proceed = () => {
        if (!choice) return setError({ kind: 'message', translationId: `${T}.target-required` });
        if (!reason.trim()) return setError({ kind: 'message', translationId: `${T}.reason-required` });
        setError(null);
        if (photo.kind === 'attach') return setStep('confirm');
        void send();
    };

    const close = () => { if (!busy) onClose(); };
    const errorLine = error && (
        <Typography color="error" variant="body2" sx={{ mt: 2 }} data-testid="placement-error">
            {error.kind === 'message' ? <Translate id={error.translationId} /> : error.message}
        </Typography>
    );

    return (
        <Dialog open onClose={close} maxWidth="sm" fullWidth>
            <DialogTitle><Translate id={`${T}.${photo.kind}-title`} /></DialogTitle>
            {step === 'choose' ? (
                <DialogContent>
                    <Box display="flex" gap={2} alignItems="flex-start">
                        <PlacementPhoto bytes={bytes} />
                        {sourceLabel && (
                            <Typography variant="body2" color="text.secondary">
                                <Translate id="hospital.patient.photo-placement.now-on" data={{ where: sourceLabel }} />
                            </Typography>
                        )}
                    </Box>
                    <Typography variant="subtitle2" sx={{ mt: 2 }}><Translate id={`${T}.choose-target`} /></Typography>
                    {targets.length === 0 && (
                        <Typography variant="body2"><Translate id={`${T}.no-target`} /></Typography>
                    )}
                    <RadioGroup value={choice ? choiceKey(choice) : ''}
                        onChange={(_event, value) => {
                            const [groupRecordUuid, field] = value.split('|');
                            setChoice({ groupRecordUuid, field });
                            setError(null);
                        }}>
                        {targets.map((target) => (
                            <Box key={target.lesion.groupRecordUuid} sx={{ mt: 1.5 }}>
                                <Typography variant="body2" fontWeight={600}>{lesionTitle(target.lesion)}</Typography>
                                <LesionFacts lesion={target.lesion} />
                                {target.slots.map((slot) => (
                                    <FormControlLabel key={slot.name}
                                        data-testid={`placement-slot-${target.lesion.groupRecordUuid}-${slot.name}`}
                                        value={choiceKey({ groupRecordUuid: target.lesion.groupRecordUuid, field: slot.name })}
                                        disabled={slot.blocked !== null || busy}
                                        control={<Radio size="small" />}
                                        label={
                                            <>
                                                {slot.label}
                                                {slot.blocked === 'hidden' && (
                                                    <Typography component="span" variant="caption" color="text.secondary">
                                                        {' — '}<Translate id={`${T}.slot-hidden`} />
                                                    </Typography>
                                                )}
                                            </>
                                        } />
                                ))}
                            </Box>
                        ))}
                    </RadioGroup>
                    <TextField fullWidth multiline minRows={2} sx={{ mt: 2 }}
                        label={<Translate id={`${T}.reason`} />}
                        value={reason}
                        disabled={busy}
                        inputProps={{ 'data-testid': 'placement-reason' }}
                        onChange={(event) => { setReason(event.target.value); setError(null); }} />
                    {errorLine}
                </DialogContent>
            ) : (
                <DialogContent data-testid="placement-confirmation">
                    <Typography variant="body2" sx={{ mb: 2 }}><Translate id={`${T}.confirm-intro`} /></Typography>
                    <Box display="flex" gap={2} alignItems="flex-start">
                        <PlacementPhoto bytes={bytes} />
                        {chosen && (
                            <Box>
                                <Typography variant="caption" color="text.secondary"><Translate id={`${T}.confirm-target`} /></Typography>
                                <Typography variant="subtitle2">{lesionTitle(chosen.lesion)} · {chosenSlot?.label}</Typography>
                                <LesionFacts lesion={chosen.lesion} />
                                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                                    <Translate id={`${T}.reason`} />
                                </Typography>
                                <Typography variant="body2">{reason.trim()}</Typography>
                            </Box>
                        )}
                    </Box>
                    {errorLine}
                </DialogContent>
            )}
            <DialogActions>
                {step === 'choose' ? (
                    <>
                        <Button onClick={close} disabled={busy}><Translate id="general.cancel" /></Button>
                        <Button onClick={proceed} disabled={busy} variant="contained" data-testid="placement-continue">
                            <Translate id={photo.kind === 'move' ? `${T}.move-confirm` : `${T}.continue`} />
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={() => { setStep('choose'); setError(null); }} disabled={busy} data-testid="placement-back">
                            <Translate id={`${T}.back`} />
                        </Button>
                        <Button onClick={() => void send()} disabled={busy} variant="contained" data-testid="placement-confirm">
                            <Translate id={`${T}.attach-confirm`} />
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
